import React, { useState, useMemo } from 'react';
import clsx from 'clsx';

const sizes = {
  sm: 'px-3 py-1.5 text-xs h-8',
  md: 'px-3.5 py-2 text-sm h-9',
  lg: 'px-4 py-2.5 text-base h-10',
};

/**
 * Type-to-search select for large option lists. Filters in JS and renders only
 * the top `maxResults` matches, so a 16k-option list stays responsive (vs a
 * native <select> that mounts every option). Options: [{ value, label }].
 */
export function SearchableSelect({
  label,
  value,
  onChange,
  options = [],
  placeholder = 'Search…',
  error,
  required,
  size = 'md',
  maxResults = 50,
}) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);

  const selected = useMemo(
    () => options.find((o) => String(o.value) === String(value)),
    [options, value]
  );

  const q = query.trim().toLowerCase();
  const { matches, total } = useMemo(() => {
    const all = q
      ? options.filter((o) => (o.label || '').toLowerCase().includes(q))
      : options;
    return { matches: all.slice(0, maxResults), total: all.length };
  }, [options, q, maxResults]);

  const select = (opt) => {
    onChange(opt.value);
    setQuery('');
    setOpen(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      // Don't submit the surrounding form; pick the top match instead.
      e.preventDefault();
      if (matches.length > 0) select(matches[0]);
    } else if (e.key === 'Escape') {
      setOpen(false);
      e.currentTarget.blur();
    }
  };

  return (
    <div className="w-full">
      {label && (
        <label className="block text-sm font-medium text-slate-600 mb-1.5">
          {label}
        </label>
      )}
      <div className="relative">
        <input
          type="text"
          autoComplete="off"
          value={open ? query : (selected ? selected.label : '')}
          placeholder={selected ? selected.label : placeholder}
          onFocus={() => {
            setOpen(true);
            setQuery('');
          }}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onBlur={() => setOpen(false)}
          onKeyDown={handleKeyDown}
          className={clsx(
            'w-full bg-white border rounded text-slate-800 placeholder-slate-400 transition-colors duration-100',
            'focus:outline-none focus:ring-1 cursor-text',
            error
              ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
              : 'border-slate-300 focus:border-wrg-blue-500 focus:ring-wrg-blue-500',
            sizes[size]
          )}
        />
        {open && (
          // preventDefault on mousedown so clicking an option doesn't blur the
          // input before the click registers.
          <div
            onMouseDown={(e) => e.preventDefault()}
            className="absolute z-20 mt-1 w-full max-h-60 overflow-auto bg-white border border-slate-200 rounded shadow-lg"
          >
            {matches.length === 0 ? (
              <div className="px-3 py-2 text-xs text-slate-400">
                {options.length === 0 ? 'Loading…' : 'No matches'}
              </div>
            ) : (
              <>
                {matches.map((o) => (
                  <button
                    type="button"
                    key={o.value}
                    onClick={() => select(o)}
                    className={clsx(
                      'block w-full text-left px-3 py-2 text-xs hover:bg-slate-100',
                      String(o.value) === String(value)
                        ? 'bg-wrg-blue-50 text-wrg-blue-700 font-medium'
                        : 'text-slate-700'
                    )}
                  >
                    {o.label}
                  </button>
                ))}
                {total > matches.length && (
                  <div className="px-3 py-2 text-[11px] text-slate-400 border-t border-slate-100">
                    Showing {matches.length} of {total} — keep typing to narrow.
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>
      {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
    </div>
  );
}
